import { useAssistantNavigation } from "@/hooks/useAssistantNavigation";
import FeedbackButton from "@/components/FeedbackButton";
import DocumentationLayout from '@/components/documentation/DocumentationLayout';
import { groupNavigationItems } from "@/utils/navigationGroups";
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  Calendar,
  CheckSquare,
  LayoutDashboard,
  User as UserIcon,
  LogOut,
  UserCheck,
  Bell,
  AlertTriangle,
  FolderOpen,
  ChevronDown,
  Mail,
  Code,
  Plane,
  BookOpen,
  Bot,
  Clock,
  Shirt,
  Wrench
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { GraduationCap } from "lucide-react";
import { usePortal } from "@/lib/PortalContext";
import { useAuth } from "@/lib/AuthContext";
import ProfileSetupPrompt from "./components/ProfileSetupPrompt";
import NotificationCenter from "./components/NotificationCenter";
import NativeHeader from "./components/native/NativeShell";
import { FeaturedEventCard } from "./components/native/BottomTabBar";
import BottomTabBar from "./components/native/BottomTabBar";
import { isAppLayout } from "@/lib/native";
import PushNotificationBanner from "./components/PushNotificationBanner";
import WebPushSettings from "./components/WebPushSettings";
import AppearanceSwitch from "@/components/AppearanceSwitch";
import DarkModeToggle from "@/components/DarkModeToggle";
import BrandLogo from "@/components/BrandLogo";
import { useFeaturedEvent } from "@/hooks/useFeaturedEvent";
import { displayName } from "@/utils/displayName";

function ClockInBanner() {
  const { user } = useAuth();
  const { event, type } = useFeaturedEvent(user);
  const activeConfirmedEvent = type === 'current' ? event : null;

  if (!activeConfirmedEvent) return null;

  return (
    <div className="bg-emerald-50 border-b border-emerald-200">
      <Link
        to={createPageUrl("clock")}
        className="flex items-center gap-3 px-4 py-2.5 text-emerald-800 hover:bg-emerald-100 transition-colors"
      >
        <Clock className="w-4 h-4 flex-shrink-0" />
        <span className="text-sm font-medium flex-1">
          {activeConfirmedEvent.name} is on today. Tap to clock in or out.
        </span>
        <span className="text-xs font-semibold text-white bg-emerald-600 px-3 py-1 rounded-md flex-shrink-0">
          Clock In / Out
        </span>
      </Link>
    </div>
  );
}

function NavigationCollapse() {
  const { open, toggleSidebar } = useSidebar();
  return <button type="button" onClick={toggleSidebar} aria-expanded={open}
    aria-label={open ? 'Collapse navigation' : 'Expand navigation'}
    title={open ? 'Collapse navigation' : 'Expand navigation'}
    className="navigation-collapse shrink-0 hidden md:flex items-center justify-center h-10 w-10 rounded-md">
    <ChevronDown className={`w-5 h-5 ${open ? 'rotate-90' : '-rotate-90'}`} />
  </button>;
}

export default function Layout({ children, currentPageName }) {
  const assistantNavigation = useAssistantNavigation();
  const location = useLocation();
  const documentation = /^\/documentation(?:\/|$)/i.test(location.pathname);
  const navigate = useNavigate();
  const profilePromptDismissed = false;
  const native = isAppLayout();

  const { user, logout, impersonating, stopImpersonating } = useAuth();

  const handleLogout = () => {
    logout();
  };

  const { isManagerView, isActualAdmin, portalMode, setPortalMode } = usePortal();

  const isProfileComplete = user?.profile_complete ?? true;
  const missingFields = user?.profile_missing_fields ?? [];
  const needsPassportUpload = missingFields.includes("passport_upload");

  const showProfilePrompt = !location.pathname.toLowerCase().includes("profile") && !location.pathname.toLowerCase().includes("availability");

  const managerNavItems = [
    { title: "Dashboard", url: createPageUrl("dashboard"), icon: LayoutDashboard },
    { title: "Events", url: createPageUrl("events"), icon: Calendar },
    { title: "Tasks", url: createPageUrl("tasks"), icon: CheckSquare },
    { title: "Staff", url: createPageUrl("staff"), icon: UserCheck },
    { title: "Documents", url: createPageUrl("document-management"), icon: FolderOpen },
    { title: "Notifications", url: createPageUrl("notification-settings"), icon: Bell },
    { title: "Clock In/Out", url: createPageUrl("clock"), icon: Clock },
    { title: "Flights", url: createPageUrl("flights"), icon: Plane },
    // SKILLS_MATRIX_REMOVED: the old Skills Matrix page was removed; planning skills now live in Admin.
    { title: "Notification Preferences", url: createPageUrl("notification-preferences"), icon: Mail },
    { title: "Developer", url: createPageUrl("admin"), icon: Code },
    { title: "Uniform requests", url: "/uniform-requests", icon: Shirt },
    { title: "Faulty Equipment", url: "/faulty-equipment", icon: Wrench },
    { title: "Documentation", url: "/documentation", icon: BookOpen },
  ];

  const freelancerNavItems = [
    ...(user?.can_view_all_flights ? [{ title: "Flights", url: "/flights", icon: Plane }] : []),
    { title: "Dashboard", url: createPageUrl("dashboard"), icon: LayoutDashboard },
    { title: "Events", url: createPageUrl("events"), icon: Calendar },
    { title: "Tasks", url: createPageUrl("tasks"), icon: CheckSquare },
    { title: "Clock In/Out", url: createPageUrl("clock"), icon: Clock },
    { title: "My Profile", url: createPageUrl("profile"), icon: UserIcon },
    { title: "My Availability", url: createPageUrl("availability"), icon: Calendar },
    { title: "My Documents", url: createPageUrl("documents"), icon: FolderOpen },
    { title: "iHASCO Training", url: createPageUrl("training"), icon: GraduationCap },
    { title: "Notification Preferences", url: createPageUrl("notification-preferences"), icon: Bell },
    { title: "Uniform Request", url: "/uniform-requests", icon: Shirt },
    { title: "Faulty Equipment", url: "/faulty-equipment", icon: Wrench },
    { title: "Documentation", url: "/documentation", icon: BookOpen },
  ];

  const allNavItems = [...(isManagerView ? managerNavItems : freelancerNavItems), ...(assistantNavigation.visible && !assistantNavigation.chat ? [{ title: "Assistant", url: "/assistant", icon: Bot }] : [])];
  const navigationItems = (isProfileComplete || isManagerView) ? allNavItems : allNavItems.filter(item => item.url.includes("profile"));
  const navigationGroups = groupNavigationItems(navigationItems);

  return (
    <SidebarProvider className={documentation ? 'documentation-layout' : undefined}>
            <DocumentationLayout active={documentation} />
            {showProfilePrompt && !profilePromptDismissed && <ProfileSetupPrompt user={user} />}
            <div className={`min-h-screen flex w-full bg-gray-50 ${native ? "native-shell" : ""}`}>
                  {!native && <Sidebar collapsible="icon" className="border-r border-gray-200 bg-white">
          <SidebarHeader className="border-b border-gray-200 p-4 pt-[calc(env(safe-area-inset-top)+1rem)]">
            <div className="sidebar-expanded flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="bg-black rounded-lg p-3 flex-1">
                  <BrandLogo alt="Seiko Sports Technologies" className="w-full h-auto object-contain" width={200} height={43} />
                </div>
                {user && !native && <NotificationCenter user={user} />}
              </div>
              {isActualAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors outline-none">
                    {portalMode === "manager" ? "Manager Portal" : "Event Portal"}
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width]">
                    <DropdownMenuItem
                      onClick={() => setPortalMode("manager")}
                      className={portalMode === "manager" ? "bg-gray-100 font-medium" : ""}
                    >
                      Manager Portal
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setPortalMode("staff")}
                      className={portalMode === "staff" ? "bg-gray-100 font-medium" : ""}
                    >
                      Event Portal
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </SidebarHeader>
          
          <SidebarContent className="p-3">
            <nav aria-label="Main navigation">
            {navigationGroups.map((group) => (
            <SidebarGroup key={group.title} className="py-2">
              <SidebarGroupLabel className="mb-1 bg-gray-100 text-[11px] font-bold uppercase tracking-wider text-gray-950">{group.title}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu aria-label={group.title}>
                  {group.items.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                      tooltip={item.title}
                      asChild
                      className={`hover:bg-gray-100 transition-all duration-200 rounded-lg mb-1 ${
                        location.pathname === item.url
                          || (item.url === createPageUrl("dashboard") && location.pathname === "/")
                          || location.pathname.startsWith(item.url + '/')
                          ? 'bg-[rgb(var(--brand-yellow))] text-black hover:bg-[rgb(var(--brand-yellow-hover))]'
                          : 'text-gray-700 hover:text-gray-900'
                      }`}
                      >
                        {item.external ? (
                          <a aria-label={item.title} href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3">
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.title}</span>
                          </a>
                        ) : (
                          <Link aria-label={item.title} to={item.url} className="flex items-center gap-3 px-4 py-3">
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.title}</span>
                          </Link>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            ))}
            </nav>
          </SidebarContent>

          <SidebarFooter className="border-t border-gray-200 p-0">
            {assistantNavigation.visible && assistantNavigation.chat && <div className="p-3 border-b border-gray-200" aria-label="Assistant access">
              <Link to="/assistant" aria-label="Open Assistant" title="Assistant" className="flex min-h-14 items-center justify-center gap-3 rounded-lg bg-[rgb(var(--brand-yellow))] px-3 py-3 text-black hover:brightness-95 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
                <Bot className="h-6 w-6 shrink-0" /><span className="sidebar-expanded text-base font-semibold">Assistant</span>
              </Link>
            </div>}
            <div>
              <Link aria-label="Your profile" to={createPageUrl("profile")} className="sidebar-profile flex items-center gap-3 p-3 hover:bg-gray-100 transition-colors">
                {user?.profile_photo_url ? (
                  <img
                    src={user.profile_photo_url}
                    alt={`${displayName(user) || 'User'}'s profile photo`}
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.parentElement.querySelector('[data-fallback]').style.display = '';
                    }}
                  />
                ) : null}
                <div
                  data-fallback
                  className="w-10 h-10 bg-gradient-to-br from-[rgb(var(--brand-yellow))] to-[rgb(var(--brand-yellow-hover))] rounded-full flex items-center justify-center"
                  style={user?.profile_photo_url ? { display: 'none' } : undefined}
                >
                  <span className="text-black font-semibold text-sm">
                    {displayName(user).charAt(0) || 'U'}
                  </span>
                </div>
                <div className="sidebar-expanded flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">
                    {displayName(user) || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </Link>
              <div className="sidebar-bottom p-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
              <div className="sidebar-expanded"><AppearanceSwitch /></div>
              <div className="sidebar-actions flex items-center gap-1">
              <DarkModeToggle />
              <button
                aria-label="Logout"
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span className="sidebar-expanded text-sm font-medium">Logout</span>
              </button>
              <NavigationCollapse />
              </div>
              </div>
            </div>
          </SidebarFooter>
        </Sidebar>}

        <main className="flex-1 flex flex-col overflow-hidden">
          {native && <NativeHeader />}
          {!native && (
            <div
              aria-hidden="true"
              className="shrink-0 bg-white"
              style={{ height: "var(--safe-area-top)" }}
            />
          )}
          {impersonating && (
            <div className="bg-indigo-600 text-white px-4 py-2 flex items-center justify-between">
              <span className="text-sm font-medium">
                Impersonating: {displayName(user)}
              </span>
              <button
                onClick={stopImpersonating}
                className="text-sm font-medium bg-white text-indigo-600 px-3 py-1 rounded-md hover:bg-indigo-50 transition-colors"
              >
                Stop Impersonating
              </button>
            </div>
          )}
          {isManagerView && !native && (
            <div data-brand-portal-banner="" className="border-b border-amber-300 bg-amber-400 px-4 py-1.5 text-black">
              <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wide">
                <AlertTriangle className="h-3.5 w-3.5" />
                Manager Portal
              </div>
            </div>
          )}
          {!native && (
            <header data-brand-header="" className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between md:hidden">
              <div className="flex items-center gap-4">
                <SidebarTrigger aria-label="Toggle navigation menu" className="hover:bg-gray-100 p-2 rounded-lg transition-colors duration-200 text-gray-900 h-10 w-10 [&_svg]:size-5" />
                <div className="bg-black rounded-md px-2 py-1">
                  <BrandLogo compact alt="Seiko Sports Technologies" className="h-5 w-auto object-contain" width={100} height={22} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <DarkModeToggle />
                {user && <NotificationCenter user={user} />}
              </div>
            </header>
          )}

          {(!native || !isProfileComplete) && <div
            className="grid transition-[grid-template-rows] duration-300 ease-in-out"
            style={{ gridTemplateRows: user && !isProfileComplete ? "1fr" : "0fr" }}
          >
            <div className="overflow-hidden">
              <div className="bg-amber-50 border-b border-amber-200">
                <Link
                  to={needsPassportUpload ? "/profile/travel" : createPageUrl("profile")}
                  className="flex items-center gap-2 px-4 py-2.5 text-amber-800 hover:bg-amber-100 transition-colors"
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium">
                    {needsPassportUpload
                      ? "You must upload a copy of your passport — go to Travel & Documents to upload it."
                      : "Your profile is incomplete — you won't be able to apply to events until it's finished."}
                  </span>
                </Link>
              </div>
            </div>
          </div>}

          {(location.pathname === "/" || location.pathname.startsWith("/dashboard") || (!native && location.pathname.startsWith("/event"))) && (
            <PushNotificationBanner user={user} />
          )}

          {user && !isManagerView && <ClockInBanner />}
          <WebPushSettings />

          <div className={`flex-1 overflow-auto bg-gray-50 ${native ? "native-content" : ""} ${documentation ? 'documentation-content' : ''}`}>
            {native && (location.pathname === "/" || location.pathname === "/dashboard") && <FeaturedEventCard />}
            {children}
          </div>
        </main>
      </div>
      {native && <BottomTabBar />}
      {user && <FeedbackButton native={native} />}
    </SidebarProvider>
  );
}
